import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ClassificationFilter from './filters/ClassificationFilter';
import StatusFilter from './filters/StatusFilter';
import TagsFilter from './filters/TagsFilter';
import SearchInputFilter from './filters/SearchInputFilter';
import ActiveFilterItemIndicator from './filters/ActiveFilterItemIndicator';
import ActiveFiltersContainer from './filters/ActiveFiltersContainer';
import ClassificationScoreFilter from './filters/ClassificationScoreFilter';
import { usePlatformData } from '../hooks/usePlatformData';
import ProjectIdInputFilter from './filters/ProjectIdFilter';
import { useFilterState } from './filters/useFilterState';

const STORAGE_KEY = 'document-filters';

interface DocumentFiltersProps {
  onFilterChange: (filters: DocumentFilters & { uploadId?: string }) => void;
}

export interface DocumentFilters {
  search: string;
  projectId: string;
  status: 'done' | 'pending' | 'error' | 'invalid' | '';
  classification: string | null;
  classificationScore: string;
  tags: string[];
}

const defaultFilters: DocumentFilters = {
  search: '',
  projectId: '',
  status: '',
  classification: null,
  classificationScore: '',
  tags: [],
};

const STATUS_VALUES: DocumentFilters['status'][] = ['done', 'pending', 'error', 'invalid'];

// Seed filters from URL params (e.g. a deep link from Project View to failed
// documents). Only recognised keys are applied, and `status` is validated
// against the filter union so an arbitrary query value can't be injected.
function urlOverrides(searchParams: URLSearchParams): Partial<DocumentFilters> | undefined {
  const overrides: Partial<DocumentFilters> = {};
  const projectId = searchParams.get('projectId');
  if (projectId) {
    overrides.projectId = projectId;
  }
  const status = searchParams.get('status');
  if (status && STATUS_VALUES.includes(status as DocumentFilters['status'])) {
    overrides.status = status as DocumentFilters['status'];
  }
  return Object.keys(overrides).length > 0 ? overrides : undefined;
}

export default function DocumentFilters({ onFilterChange }: DocumentFiltersProps) {
  const platformData = usePlatformData();
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, setFilters, clear } = useFilterState<DocumentFilters>({
    storageKey: STORAGE_KEY,
    defaults: defaultFilters,
    onFilterChange,
    overrides: urlOverrides(searchParams),
  });

  const onlyIds = searchParams.getAll('ids');
  const uploadId = searchParams.get('uploadId');
  const attention = searchParams.get('attention') === '1' || searchParams.get('attention') === 'true';

  // Consume the seed params once applied. Otherwise removing the Project ID
  // or Status chip only clears in-memory/sessionStorage state — the URL still
  // carries them, so a refresh reseeds `overrides` and resurrects the filter
  // the user just removed.
  useEffect(() => {
    if (!urlOverrides(searchParams)) return;
    const next = new URLSearchParams(searchParams);
    next.delete('projectId');
    next.delete('status');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (value: string) => {
    const newFilters = { ...filters, search: value };
    setFilters(newFilters);
  };

  const handleClassificationChange = (value: string) => {
    const classification = value === '' ? null : value;
    const newFilters = { ...filters, classification };
    setFilters(newFilters);
  };

  const handleStatusChange = (value: string) => {
    const newFilters = {
      ...filters,
      status: value as DocumentFilters['status'],
    };
    setFilters(newFilters);
  };

  const handleTagChange = (value: string) => {
    const selectedTag = value;
    if (!selectedTag) {
      const newFilters = { ...filters, tags: [] };
      setFilters(newFilters);
      return;
    }

    if (!filters.tags.includes(selectedTag)) {
      const newFilters = {
        ...filters,
        tags: [...filters.tags, selectedTag],
      };
      setFilters(newFilters);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const newFilters = {
      ...filters,
      tags: filters.tags.filter((tag) => tag !== tagToRemove),
    };
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    clear();
    setSearchParams(new URLSearchParams());
  };

  const handleClassificationScoreChange = (value: string) => {
    const newFilters = { ...filters, classificationScore: value };
    setFilters(newFilters);
  };

  const handleProjectIdChange = (value: string) => {
    const newFilters = { ...filters, projectId: value };
    setFilters(newFilters);
  };

  const hasActiveFilters =
    filters.search ||
    filters.projectId ||
    filters.status ||
    filters.classification ||
    filters.classificationScore ||
    onlyIds.length > 0 ||
    filters.tags.length > 0 ||
    uploadId ||
    attention;

  return (
    <div className="rounded-lg mb-6">
      {/* Filter content - hidden on mobile (mobile-expand toggle removed); always visible from sm+. */}
      <div className="hidden sm:block">
        {!attention && (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            <SearchInputFilter filters={filters} handleSearchChange={handleSearchChange} />
            <ProjectIdInputFilter filters={filters} handleProjectIdChange={handleProjectIdChange} />

            <StatusFilter filters={filters} handleStatusChange={handleStatusChange} />
            <ClassificationFilter
              filters={filters}
              handleClassificationChange={handleClassificationChange}
              categoriesWithGroups={[
                {
                  label: 'Document Categories',
                  options:
                    platformData?.categories?.data?.documentCategories
                      .map((item) => ({
                        label: item,
                        value: item,
                      }))
                      ?.sort((a, b) => a.label.localeCompare(b.label)) ?? [],
                },
                {
                  label: 'Page Categories',
                  options:
                    platformData?.categories?.data?.pageCategories
                      .map((item) => ({
                        label: item,
                        value: item,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label)) ?? [],
                },
              ]}
            />
            <ClassificationScoreFilter filters={filters} onChange={handleClassificationScoreChange} />

            <TagsFilter filters={filters} handleTagChange={handleTagChange} />
          </div>
        )}

        {/* Active Filters Display */}
        <ActiveFiltersContainer handleClearFilters={handleClearFilters} hasActiveFilters={!!hasActiveFilters}>
          {!attention && filters.search && (
            <ActiveFilterItemIndicator
              label="Search"
              value={filters.search}
              className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              onRemove={() => handleSearchChange('')}
            />
          )}
          {!attention && filters.projectId && (
            <ActiveFilterItemIndicator
              label="Project ID"
              value={filters.projectId}
              className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
              onRemove={() => handleProjectIdChange('')}
            />
          )}

          {!attention && filters.status && (
            <ActiveFilterItemIndicator
              label="Status"
              value={filters.status}
              className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
              onRemove={() => handleStatusChange('')}
            />
          )}

          {!attention && filters.classification && (
            <ActiveFilterItemIndicator
              label="Classification"
              value={filters.classification}
              className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
              onRemove={() => handleClassificationChange('')}
            />
          )}

          {!attention && filters.classificationScore && (
            <ActiveFilterItemIndicator
              label="Classification Score"
              value={filters.classificationScore}
              className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              onRemove={() => handleClassificationScoreChange('')}
            />
          )}

          {!attention &&
            filters.tags.map((tag) => (
              <ActiveFilterItemIndicator
                key={tag}
                label="Tag"
                value={tag}
                className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
                onRemove={() => handleRemoveTag(tag)}
              />
            ))}

          {onlyIds.map((id) => (
            <ActiveFilterItemIndicator
              key={id}
              label="ID"
              value={`${id.slice(0, 4)}...${id.slice(-4)}`}
              className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
              onRemove={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('ids', id);
                setSearchParams(newParams);
              }}
            />
          ))}

          {uploadId && (
            <ActiveFilterItemIndicator
              label="Upload ID"
              value={uploadId}
              className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
              onRemove={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('uploadId');
                setSearchParams(newParams);
              }}
            />
          )}

          {attention && (
            <ActiveFilterItemIndicator
              label="Needs attention"
              value="yes"
              className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
              onRemove={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('attention');
                setSearchParams(newParams);
              }}
            />
          )}
        </ActiveFiltersContainer>
      </div>
    </div>
  );
}
